import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

export default class extends Controller {
  static targets = [
    "siteBtn",
    "workerBtn",
    "siteSelector",
    "workerSelector",
    "month",
    "detailPanel",
    "detailDate",
    "detailSite",
    "detailWorker",
    "cellMessage",
    "detailContent",
    "formWorkPerformance",
    "formWorkerId",
    "formDate",
    "matrix",
    "timeSlice",
    "sliceStart",
    "sliceEnd",
    "workTime",
    "manDay",
    "toolTime",
    "toolManDay"
  ]

  connect() {
    this.selectedCell = null

    const url = new URL(window.location.href)
    this.mode = url.searchParams.get("mode") || "project"

    if (this.mode === "worker") {
      this.activateWorkerMode()
    } else {
      this.activateProjectMode()
    }
  }

  matrixTargetConnected(element) {
    this.reselectCell()
  }

  reselectCell() {
    if (!this.activeDate) return

    let selector = `.matrix-cell[data-date="${this.activeDate}"]`
    if (this.mode === "project") {
      if (this.activeWorkerId) {
        selector += `[data-worker-id="${this.activeWorkerId}"]`
      }
    } else {
      if (this.activeProjectPartId) {
        selector += `[data-project-part-id="${this.activeProjectPartId}"]`
      }
    }

    const cell = this.matrixTarget.querySelector(selector)
    if (cell) {
      cell.classList.add("selected-cell")
      this.selectedCell = cell
    }
  }

  showProject() {
    this.activateProjectMode()
    this.switchMode("project")
  }

  showWorker() {
    this.activateWorkerMode()
    this.switchMode("worker")
  }

  switchMode(mode) {
    this.mode = mode
    this.clearSelection()

    const url = new URL(window.location.href)

    const monthValue = this.monthTarget.value
    if (monthValue) {
      const [year, month] = monthValue.split("-")
      url.searchParams.set("year", year)
      url.searchParams.set("month", month)
    }

    url.searchParams.set("mode", mode)
    url.searchParams.delete("summary_project_part_id")
    url.searchParams.delete("summary_worker_id")

    Turbo.visit(url.toString(), { frame: "matrix" })
    this.resetSummary()
  }

  activateProjectMode() {
    this.siteBtnTarget.classList.add("active")
    this.workerBtnTarget.classList.remove("active")

    this.siteSelectorTarget.style.display = ""
    this.workerSelectorTarget.style.display = "none"
  }

  activateWorkerMode() {
    this.workerBtnTarget.classList.add("active")
    this.siteBtnTarget.classList.remove("active")

    this.siteSelectorTarget.style.display = "none"
    this.workerSelectorTarget.style.display = ""
  }

  updateMonth() {
    const value = this.monthTarget.value
    if (!value) return

    this.clearSelection()

    const [year, month] = value.split("-")
    const url = new URL(window.location.href)

    url.searchParams.set("mode", this.mode)
    url.searchParams.set("year", year)
    url.searchParams.set("month", month)

    Turbo.visit(url.toString(), { frame: "matrix" })
    this.resetSummary()
  }

  selectorChange(event) {
    this.clearSelection()
    const url = new URL(window.location.href)

    const monthValue = this.monthTarget.value
    if (monthValue) {
      const [year, month] = monthValue.split("-")
      url.searchParams.set("year", year)
      url.searchParams.set("month", month)
    }

    url.searchParams.set("mode", this.mode)

    if (this.mode === "project") {
      url.searchParams.set("project_part_id", event.target.value)
    } else {
      url.searchParams.set("worker_id", event.target.value)
    }

    Turbo.visit(url.toString(), { frame: "matrix" })
    this.resetSummary()
  }

  cellClick(event) {
    const td = event.target.closest(".matrix-cell")
    if (!td) return

    const url = td.dataset.url

    if (this.selectedCell) {
      this.selectedCell.classList.remove("selected-cell")
    }

    td.classList.add("selected-cell")
    this.selectedCell = td

    this.activeWorkerId = td.dataset.workerId
    this.activeProjectPartId = td.dataset.projectPartId
    this.activeDate = td.dataset.date

    if (url && url.length > 0) {
      Turbo.visit(url, { frame: "form" })
    } else {
      this.prepareNewForm(td)
      this.showForm()
    }

    this.updateSummary(td)
    this.updateDetail(td)
  }

  prepareNewForm(td) {
    if (!this.hasFormWorkPerformanceTarget) return

    const form = this.formWorkPerformanceTarget

    form.querySelectorAll("input, select, textarea").forEach(el => {
      if (el.type === "checkbox" || el.type === "radio") {
        el.checked = false
      } else {
        if (!el.classList.contains("btn")) {
          el.value = ""
        }
      }
    })

    const setFieldValue = (name, value) => {
      const field = form.querySelector(`[name='performance[${name}]']`)
      if (field && value) field.value = value
    }

    setFieldValue('time_slice_id', td.dataset.timeSliceId)
    setFieldValue('work_type_id', td.dataset.workTypeId)
    setFieldValue('tool_id', td.dataset.toolId)
    setFieldValue('slice_start', td.dataset.sliceStart)
    setFieldValue('slice_end', td.dataset.sliceEnd)
    setFieldValue('work_time_span_in_decimal', td.dataset.workTime)
    setFieldValue('tool_time_span_in_decimal', td.dataset.toolTime)
    setFieldValue('work_man_days', td.dataset.manDay)

    form.action = "/work_performances"
    form.method = "post"

    const modeInput = form.querySelector("input[name='mode']")
    if (modeInput) {
      modeInput.value = this.mode
    }

    const monthValue = this.monthTarget.value
    if (monthValue) {
      const [year, month] = monthValue.split("-")
      const yearInput = form.querySelector("input[name='year']")
      const monthInput = form.querySelector("input[name='month']")
      if (yearInput) yearInput.value = year
      if (monthInput) monthInput.value = month
    }

    const methodInput = form.querySelector("input[name='_method']")
    if (methodInput) {
      methodInput.disabled = true
    }

    const attendanceId = td.dataset.attendanceId
    const projectPartId = td.dataset.projectPartId || this.siteSelectorTarget?.querySelector("select")?.value

    const attendanceField =
      form.querySelector("input[name='performance[attendance_id]']")

    const projectPartField =
      form.querySelector("input[name='performance[project_part_id]']")

    if (attendanceField && attendanceId) {
      attendanceField.value = attendanceId
    } else {
      if (attendanceField) attendanceField.value = ""
    }

    if (projectPartField && projectPartId) {
      projectPartField.value = projectPartId
    }

    if (this.hasFormWorkerIdTarget) {
      this.formWorkerIdTarget.value = td.dataset.workerId || this.workerSelectorTarget?.querySelector("select")?.value || ""
    }

    if (this.hasFormDateTarget) {
      this.formDateTarget.value = td.dataset.date || ""
    }
  }

  updateSummary(td) {
    const summaryUrl = new URL(window.location)

    summaryUrl.searchParams.delete("summary_project_part_id")
    summaryUrl.searchParams.delete("summary_worker_id")

    if (this.mode === "project") {
      summaryUrl.searchParams.set("summary_worker_id", td.dataset.workerId)
    } else {
      summaryUrl.searchParams.set("summary_project_part_id", td.dataset.projectPartId)
    }

    const monthValue = this.monthTarget.value
    if (monthValue) {
      const [year, month] = monthValue.split("-")
      summaryUrl.searchParams.set("year", year)
      summaryUrl.searchParams.set("month", month)
    }

    Turbo.visit(summaryUrl.toString(), { frame: "summary" })
  }

  resetSummary() {
    const summaryUrl = new URL(window.location)
    summaryUrl.searchParams.delete("summary_project_part_id")
    summaryUrl.searchParams.delete("summary_worker_id")

    const monthValue = this.monthTarget.value
    if (monthValue) {
      const [year, month] = monthValue.split("-")
      summaryUrl.searchParams.set("year", year)
      summaryUrl.searchParams.set("month", month)
    }

    Turbo.visit(summaryUrl.toString(), { frame: "summary" })
  }

  updateDetail(td) {
    const date = td.dataset.date

    let workerName = ""
    let projectName = ""

    if (this.mode === "project") {
      workerName = td.closest("tr").querySelector(".name-col")?.textContent
      projectName = this.siteSelectorTarget?.querySelector("select")?.selectedOptions[0]?.text
    } else {
      projectName = td.closest("tr").querySelector(".name-col")?.textContent
      workerName = this.workerSelectorTarget?.querySelector("select")?.selectedOptions[0]?.text
    }

    this.showDetail({ date, workerName, projectName })
  }

  showDetail({ date, workerName, projectName }) {
    if (this.hasDetailDateTarget) {
      this.detailDateTarget.textContent = date
    }

    if (this.hasDetailWorkerTarget) {
      this.detailWorkerTarget.textContent = workerName || ""
    }

    if (this.hasDetailSiteTarget) {
      this.detailSiteTarget.textContent = projectName || ""
    }

    if (this.hasDetailPanelTarget) {
      this.detailPanelTarget.style.display = "block"
    }
    if (this.hasCellMessageTarget) {
      this.cellMessageTarget.style.display = "none"
    }
    if (this.hasDetailContentTarget) {
      this.detailContentTarget.style.display = "block"
      this.detailContentTarget.style.removeProperty("display")
      this.detailContentTarget.style.display = "block"
    }
  }

  hideDetail() {
    this.cellMessageTarget.style.display = "block"
    this.detailContentTarget.style.display = "none"
  }

  showForm() {
    if (this.hasCellMessageTarget) {
      this.cellMessageTarget.style.display = "none"
    }

    if (this.hasDetailContentTarget) {
      this.detailContentTarget.style.display = "block"
    }
  }

  clearSelection() {
    this.hideDetail()

    this.activeWorkerId = null
    this.activeProjectPartId = null
    this.activeDate = null
    if (this.selectedCell) {
      this.selectedCell.classList.remove("selected-cell")
      this.selectedCell = null
    }
  }
}